import { PinForDays } from '@admin/engagement/incentives/configureIncentive/types'
import AppPaths from '@globals/paths'
import { tabletQuery } from '@globals/themes/themeConstants'
import { useApiActionToast } from '@globals/toaster/useToasterStore'
import {
  AwardType,
  AwardTypeEmbeds,
  AwardTypeParams,
  MediaVariantEnum,
  createAwardType,
  editAwardType,
  getAwardType,
  initiateAwardUpload,
} from '@kudos/http-client'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useApiError } from '@shared/errors/useApiError'
import { Selectable } from '@shared/filters/selections'
import { useInvalidateQuery } from '@shared/hooks/useInvalidateQuery'
import useMediaUrl from '@shared/hooks/useMediaUrl'
import { getSendToFromTenantMemberAndTeams } from '@shared/utils/getSendToFromTenantMemberAndTeams'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useT } from '@transifex/react'
import { FieldValues, UseFormSetError } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { AwardFormData } from './useConfigureAwardTypeForm'

export const AWARD_TYPE_QK = 'awardTypes'
const useAwardTypeData = (
  awardTypeId?: string,
  embeds: AwardTypeEmbeds[] = [
    'attachmentUrls',
    'pointsBankName',
    'pointsBankBalance',
    'senders',
    'nominators',
    'candidates',
    'stats',
  ],
) => {
  const awardTypeQueryKey = [AWARD_TYPE_QK, awardTypeId]
  const isEdit = !!awardTypeId
  const isTablet = useMediaQuery(tabletQuery)
  const [showSuccess] = useApiActionToast()
  const { handleApiError } = useApiError()
  const invalidateQuery = useInvalidateQuery()
  const t = useT()
  const navigate = useNavigate()

  const { data: awardTypeData, isFetched: isAwardTypeFetched } = useQuery({
    queryKey: awardTypeQueryKey,
    queryFn: () =>
      getAwardType(awardTypeId!, {
        embeds,
      }),
    enabled: !!awardTypeId,
  })

  const { mutate: createOrSaveAwardTypeMutation } = useMutation({
    mutationFn: (params: AwardTypeParams) => {
      return isEdit ? editAwardType(awardTypeId, params) : createAwardType(params)
    },
    onSuccess: async (data: AwardType) => {
      showSuccess(isEdit ? t('Award successfully updated') : t('Award successfully created'))
      await invalidateQuery(awardTypeQueryKey)
      if (data.nomination) {
        navigate(AppPaths.tenant.admin.viewNominationAwardType(data.id))
      } else {
        navigate(AppPaths.tenant.admin.viewAwardType(data.id))
      }
    },
    onError: error => handleApiError(error),
  })

  const initiateMediaUploadMutation = useMutation({
    mutationFn: ({
      originalFilename,
      contentType,
    }: {
      originalFilename: string
      contentType: string
    }) => initiateAwardUpload({ originalFilename, contentType }),
  })

  const { url: awardTypeMediaUrl } = useMediaUrl(
    awardTypeData?.attachmentUrls,
    isTablet ? MediaVariantEnum.AWARD_BADGE_MOBILE : MediaVariantEnum.AWARD_BADGE_DESKTOP,
  )

  const createOrSaveAwardType = async (
    formData: AwardFormData,
    isNomination: boolean,
    mediaObjectId: string | null,
    setError: UseFormSetError<FieldValues>,
    reset: () => void,
  ) => {
    const [nominationStartAt, nominationEndAt] = isNomination
      ? formData.dateTimeRange
      : [null, null]

    const pinForDays = () => {
      if (isNomination) {
        return formData.pinForDays === PinForDays.NEVER ? null : Number(formData.pinForDays)
      }
      return null
    }

    const candidatesAndNominators = (tenantWide: string, teamIds: Selectable[]) => {
      if (!isNomination) return []

      if (tenantWide === 'true') return []
      return teamIds.map(team => team.id)
    }

    const tenantWide = (tenantWide: string) => {
      if (!isNomination) return true
      return tenantWide === 'true'
    }

    const payload: AwardTypeParams = {
      name: formData.name,
      description: formData.description,
      points: Number.isInteger(formData.points) ? formData.points : 0,
      senders: getSendToFromTenantMemberAndTeams(formData.senders),
      pointsLocked:
        Number(formData.points) === 0 || formData.nomination ? true : formData.pointsLocked,
      budgetItemId: Number(formData.points) === 0 ? null : formData.budgetItemId || null,
      mediaObjectId,
      archived: isNomination ? false : !formData.archived,
      nomination: formData.nomination,
      nominationStartAt,
      nominationEndAt,
      notify: 'notify' in formData ? formData.notify : false,
      pinForDays: pinForDays(),
      maxNominations: 'maxNominations' in formData ? formData.maxNominations : null,
      tenantWideNominators: tenantWide(formData.tenantWideNominators),
      tenantWideCandidates: tenantWide(formData.tenantWideCandidates),
      candidates: candidatesAndNominators(formData.tenantWideCandidates, formData.candidates),
      nominators: candidatesAndNominators(formData.tenantWideNominators, formData.nominators),
    }

    createOrSaveAwardTypeMutation(payload, {
      onSuccess: () => {
        reset()
      },
      onError: error =>
        handleApiError(error, setError, [
          'name',
          'points',
          'pointsLocked',
          'senders',
          'description',
          'pointsLocked',
          'archived',
          'budgetItemId',
          'dateTimeRange',
          'candidates',
          'nominators',
        ]),
    })
  }

  return {
    awardTypeData,
    isAwardTypeFetched,
    awardTypeMediaUrl,
    createOrSaveAwardType,
    initiateMediaUploadMutation,
  }
}

export default useAwardTypeData
